import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getBowserState } from '@epic-core/common';
import BackgroundVideo from 'epic-react-common/src/components/BackgroundVideo';
import { useInView } from 'react-intersection-observer';
import { GridContainer } from '../grid';
import { convertToCommonBackgroundVideo, replaceYoutubeCookieEmbeds } from '../../../utils';
import { LocalVideoOptions, LocalVideoOptionsArray } from '../../../types';
import { Image } from '../images';
import {WithCaption, WithCaptionProps} from '../../wraps/caption/WithCaption.wrap';

const VideoWrappingContainer = styled.div<{ height?: number }>`
    position: relative;
    overflow: hidden;
    height: 100%;
    width: 100%;

    &.with-gradient {
        &:after {
            content: '';
            position: relative;
            top: 0;
            left: 0;
            width: 100%;
            height: 75%;
            background: linear-gradient(
                180deg,
                ${(p) => p.theme.palette.background.default} 0%,
                rgba(0, 0, 0, 0) 100%
            );
        }
    }
`;

export const VideoRatioWrapper = styled.div`
    padding-bottom: 56.25%;
    height: 0;
    position: relative;
    > div {
        position: absolute;
        width: 100%;
        height: 100%;
        left: 0;
        top: 0;
    }
`;

const ContentContainer = styled.div<{ height?: number }>`
    position: relative;
    overflow: hidden;
    z-index: 3;
    height: 100%;
    width: 100%;
`;

const VideoContainer = styled.div`
    background-color: #000;
    position: absolute;
    height: 100%;
    width: 100%;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
`;
const VideoWrapper = styled.div`
    width: 100%;
    max-width: 100%;
    position: relative;
    height: 100%;
    inset: 0;
    & > div {
        width: 100%;
        height: 100%;
        max-width: 100%;
        position: absolute;
        top: 0;
        left: 0;
        z-index: 0;
        opacity: 1;
        .screen {
            position: absolute;
            margin: auto;
            .html-video-embed {
                height: 100%;
                width: 100%;
                video {
                    display: inline-block;
                    vertical-align: baseline;
                }
            }
        }
    }
`;

export interface VideoProps {
    children?: React.ReactNode;
    classes?: string;
    autoplay?: boolean;
    forcePlay?: boolean;
    enableRatioHelper?: boolean;
    mute?: boolean;
    mediaOptions?: LocalVideoOptions | LocalVideoOptionsArray;
    placeholderImageCaption?: WithCaptionProps;
    forceVideoMobile?: boolean;
    controls?: boolean;
    gradient?: boolean;
    disableVideoPoster?: boolean;
    disableVideoMobile?: boolean;
}

export const Video = ({
    children,
    classes,
    autoplay = true,
    enableRatioHelper = true,
    mute,
    mediaOptions,
    placeholderImageCaption,
    forceVideoMobile,
    forcePlay,
    controls,
    gradient,
    disableVideoPoster
}: VideoProps): JSX.Element | null => {
    const { inView, ref } = useInView();
    const { mobile, tablet } = getBowserState();
    const [dirty, setDirty] = useState(false);

    let shouldPlay = false;
    if (!forceVideoMobile && (mobile || tablet)) {
        shouldPlay = false;
    } else if (!inView) {
        shouldPlay = false;
    } else if (inView && autoplay) {
        shouldPlay = true;
    }

    const background = convertToCommonBackgroundVideo(mediaOptions);

    replaceYoutubeCookieEmbeds(background);

    if (disableVideoPoster) {
        background.image = '';
        background.tabletImage = '';
    }

    //with perfect SSR the client doesn't re-render and adjust the size of the video. this helps.
    useEffect(() => {
        if (!dirty) {
            setTimeout(() => {
                setDirty(true);
            }, 50);
        }
    }, [dirty, setDirty]);

    return (
        <VideoWrappingContainer
            ref={ref}
            className={`video-wrapper ${gradient ? 'with-gradient' : ''}`}>
            <VideoContainer className="video-container">
                <VideoWrapper
                    className={`bg-video-wrapper type-${background && background.type ? background.type : ''
                        } ${classes ? classes : ''}`}
                    title={background.title}>
                    {background && background.type && background.type === 'image' ? (
                        <WithCaption {...placeholderImageCaption}>
                            <Image src={background.image} disableRatioHelper alt={background.alt} />
                        </WithCaption>
                    ) : (
                        <BackgroundVideo
                            background={background}
                            enableRatioHelper={enableRatioHelper}
                            play={forcePlay || shouldPlay}
                            mute={mute === undefined ? true : mute}
                            noOverlay={true}
                            mobile={mobile}
                            tablet={tablet}
                            forceVideoMobile={forceVideoMobile}
                            controls={controls}
                            dirty={dirty}
                        />
                    )}
                </VideoWrapper>
            </VideoContainer>
            {children && (
                <ContentContainer className="video-content-container">
                    <GridContainer>{children}</GridContainer>
                </ContentContainer>
            )}
        </VideoWrappingContainer>
    );
};
